import { Coord, Row, COLS, ROWS, colIndex, indexToCol, coordKey } from '../types/grid.js'
import { Ship, SHIP_DEFS, ALL_SHIP_TYPES } from '../types/ship.js'

/** Get all cells occupied by a ship */
export function getShipCells(ship: Ship): Coord[] {
  const cells: Coord[] = []
  const ci = colIndex(ship.origin.col)
  const ri = ship.origin.row

  for (let i = 0; i < ship.size; i++) {
    if (ship.direction === 'horizontal') {
      cells.push({ col: indexToCol(ci + i), row: ri })
    } else {
      cells.push({ col: ship.origin.col, row: (ri + i) as Row })
    }
  }
  return cells
}

/** Check if a ship fits within the grid boundaries */
export function isWithinBounds(ship: Ship): boolean {
  const ci = colIndex(ship.origin.col)
  const ri = ship.origin.row

  if (ship.direction === 'horizontal') {
    return ci + ship.size - 1 < COLS.length
  } else {
    return ri + ship.size - 1 <= ROWS.length
  }
}

/** Check if two ships overlap */
export function shipsOverlap(a: Ship, b: Ship): boolean {
  const aCells = new Set(getShipCells(a).map(coordKey))
  return getShipCells(b).some(c => aCells.has(coordKey(c)))
}

export interface PlacementError {
  shipType: string
  reason: string
}

/**
 * Validate a full set of ship placements.
 * Returns an array of errors (empty = valid).
 * `blockedCells` — terrain cell keys (e.g. "C2") that no ship may occupy (GDD-v3 §4).
 */
export function validatePlacement(ships: Ship[], blockedCells?: Set<string>): PlacementError[] {
  const errors: PlacementError[] = []

  // Must have exactly the right ships
  const expectedTypes = ALL_SHIP_TYPES.slice().sort()
  const actualTypes = ships.map(s => s.type).sort()
  if (JSON.stringify(expectedTypes) !== JSON.stringify(actualTypes)) {
    errors.push({ shipType: '*', reason: 'Must place exactly one of each ship type' })
    return errors
  }

  for (const ship of ships) {
    const def = SHIP_DEFS[ship.type]

    // Correct size
    if (ship.size !== def.size) {
      errors.push({ shipType: ship.type, reason: `Wrong size: expected ${def.size}, got ${ship.size}` })
    }

    // Within bounds
    if (!isWithinBounds(ship)) {
      errors.push({ shipType: ship.type, reason: 'Ship extends outside the grid' })
    }

    // Not on terrain
    if (blockedCells && blockedCells.size > 0) {
      if (getShipCells(ship).some(c => blockedCells.has(coordKey(c)))) {
        errors.push({ shipType: ship.type, reason: 'Ship overlaps a terrain cell' })
      }
    }
  }

  // Check overlaps between all pairs
  for (let i = 0; i < ships.length; i++) {
    for (let j = i + 1; j < ships.length; j++) {
      if (shipsOverlap(ships[i], ships[j])) {
        errors.push({
          shipType: `${ships[i].type}/${ships[j].type}`,
          reason: 'Ships overlap',
        })
      }
    }
  }

  return errors
}
